import { Link } from 'react-router';
import { memo, ReactNode, useCallback } from 'react';
import classes from 'classnames';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import { EventCollectorProps } from 'hooks/context/EventCollector/types';
import ButtonEvent from 'hooks/context/EventCollector/events/ButtonEvent';
import styles from './Button.module.scss';
import Loader from './Loader/Loader';

export type Props = {
  type?: 'submit' | 'button';
  children: ReactNode;
  outline?: boolean;
  disabled?: boolean;
  className?: string;
  dataTest?: string;
  wide?: boolean;
  pending?: boolean;
  size?: 's' | 'm' | 'l';
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (event?: React.MouseEvent) => void;
  /* When given, makes button act as link */
  to?: string;
  /* Used when `to` is passed. Determins whether to use inner router or link to external site */
  external?: boolean;
  buttonClassName?: string;
};

function Button({
  children,
  type = 'button',
  outline = false,
  disabled = false,
  wide = false,
  pending = false,
  className,
  dataTest = 'button',
  size = 'l',
  onClick,
  onMouseEnter,
  to = '',
  external,
  buttonClassName,
  eventCollector,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);

      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new ButtonEvent({
          description:
            eventCollector?.description ?? children?.toString() ?? '',
          content: children?.toString() ?? '',
        }),
      );
    },
    [
      eventCollector?.description,
      eventCollector?.dontTrack,
      children,
      onClick,
      track,
    ],
  );

  const buttonStyle = classes(
    styles.button,
    outline && styles.outline,
    pending && styles.pending,
    styles[size],
    wide && styles.wide,
    buttonClassName,
  );

  return (
    <div
      className={classes(
        styles.container,
        styles[size],
        wide && styles.wide,
        className,
      )}
    >
      {to && !external && (
        <Link
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
          data-test={dataTest}
          to={to}
        >
          {children as React.JSX.Element}
        </Link>
      )}
      {to && external && (
        <a
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
          data-test={dataTest}
          href={to}
        >
          {children}
        </a>
      )}
      {!to && (
        <button
          data-test={dataTest}
          disabled={pending || disabled}
          // eslint-disable-next-line react/button-has-type
          type={type}
          onClick={handleClick}
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
        >
          {pending && (
            <div className={styles[`loader-wrapper-${size}`]}>
              <Loader outline={outline} />
            </div>
          )}
          {children}
        </button>
      )}
    </div>
  );
}

const ButtonMemo = memo(Button);

export default ButtonMemo;
